<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-modal"
        style="background: rgba(18, 23, 26, 0.4); backdrop-filter: blur(2px);"
        aria-hidden="true"
      />
    </Transition>

    <!-- Panel wrapper. Outside-clicks are handled here (not on the backdrop):
         this layer sits above the backdrop and covers the viewport, so a
         click handler on the backdrop div would never fire. -->
    <Transition :name="transitionName">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-modal-panel flex justify-center"
        :class="isSheetLayout ? 'items-end md:items-center md:p-4' : 'items-center p-4'"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || title"
        :aria-describedby="ariaDescribedby"
        @click.self="onOutsideClick"
      >
        <div
          ref="panelRef"
          tabindex="-1"
          class="w-full flex flex-col overflow-hidden"
          :class="sizeClass"
          :style="panelStyle"
        >
          <!-- Header -->
          <slot name="header">
            <div
              v-if="title || showClose"
              class="flex items-center justify-between px-6 py-4 shrink-0"
              style="border-bottom: 1px solid var(--border-subtle);"
            >
              <h3 class="m-0" style="font-size: var(--text-lg); color: var(--text-primary);">
                {{ title }}
              </h3>
              <button
                v-if="showClose"
                type="button"
                class="w-8 h-8 flex items-center justify-center"
                style="background: none; border: none; color: var(--text-tertiary); cursor: pointer;"
                :aria-label="closeLabel || $t('common.close')"
                @click="dismiss"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </slot>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 py-4" style="overscroll-behavior: contain;">
            <slot />
          </div>

          <!-- Footer (optional). The modal-actions class (mobile.css) adds the
               iOS home-indicator safe-area padding and lifts the row clear of
               the soft keyboard while the sheet is docked to the bottom edge. -->
          <div
            v-if="$slots.footer"
            class="modal-actions px-6 pt-4 pb-4 shrink-0"
            style="border-top: 1px solid var(--border-subtle); background: var(--bg-secondary);"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, toRef, watch } from "vue"
import { useBodyScrollLock } from "../../composables/useBodyScrollLock"
import { useFocusTrap } from "../../composables/useFocusTrap"
import { useModalBackClose } from "../../composables/useModalBackClose"

/**
 * The one modal primitive. Owns everything a dialog must get right so
 * feature components never re-implement it: Teleport to <body> (escapes the
 * PullToRefresh transform trap), the --z-modal layer, backdrop, focus trap +
 * focus restore, Escape / outside-click / Android-back dismissal, and the
 * ref-counted body scroll lock.
 */
interface Props {
  modelValue: boolean
  title?: string
  /** Accessible name when there is no title (custom header slot). */
  ariaLabel?: string
  /** Id of the element describing the dialog (e.g. a confirm prompt's message). */
  ariaDescribedby?: string
  /** Panel max width on desktop. */
  size?: "sm" | "md" | "lg"
  /** On phones dock to the bottom and slide up (EditModal-style). When false the panel is always centered. */
  mobileSheet?: boolean
  /** Allow Escape / outside click / Android back to close. */
  dismissible?: boolean
  showClose?: boolean
  closeLabel?: string
  /** Where focus lands on open. "first-input" targets the first data-entry field (desktop only). */
  initialFocus?: "panel" | "first-input"
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  mobileSheet: true,
  dismissible: true,
  showClose: true,
  initialFocus: "panel",
})

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void
  (e: "close"): void
}>()

const panelRef = ref<HTMLDivElement | null>(null)
const isMobile = ref(false)

function checkMobile() {
  isMobile.value = window.innerWidth < 768
}
onMounted(() => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
})
onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

const isSheetLayout = computed(() => props.mobileSheet)
const transitionName = computed(() => (props.mobileSheet && isMobile.value ? "modal-slide-up" : "modal-scale"))

// Literal class maps — Tailwind's scanner only sees classes written out in full
const sizeClass = computed(() => {
  const sheet = { sm: "md:max-w-sm", md: "md:max-w-lg", lg: "md:max-w-2xl" } as const
  const centered = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" } as const
  return `${(props.mobileSheet ? sheet : centered)[props.size]} max-h-[90vh]`
})

const panelStyle = computed(() => {
  const base =
    "background: var(--surface); border: 1px solid var(--border-subtle); box-shadow: var(--shadow-2xl); max-height: 90dvh;"
  const radius = props.mobileSheet && isMobile.value ? "border-radius: 8px 8px 0 0;" : "border-radius: 8px;"
  return base + radius
})

function dismiss() {
  emit("close")
  emit("update:modelValue", false)
}

function onOutsideClick() {
  if (props.dismissible) dismiss()
}

// Tab stays inside the panel; Escape dismisses (when allowed); focus returns
// to the opener on close.
useFocusTrap(panelRef, toRef(props, "modelValue"), {
  onEscape: () => {
    if (props.dismissible) dismiss()
  },
})

// Android hardware back closes the modal instead of leaving the screen
useModalBackClose(
  () => props.modelValue && props.dismissible,
  () => dismiss(),
)

// Ref-counted body scroll lock, shared with every other overlay
useBodyScrollLock(() => props.modelValue)

function focusInitial() {
  const panel = panelRef.value
  if (!panel) return
  // A wrapper (e.g. ConfirmDialog) may have placed focus on a specific
  // element inside the panel already — don't steal it.
  if (document.activeElement && panel.contains(document.activeElement)) return
  // On mobile, auto-focusing an input pops the soft keyboard immediately —
  // focus the panel instead and let the user tap a field when ready.
  if (props.initialFocus === "first-input" && !isMobile.value) {
    const firstInput =
      panel.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
      ) ?? panel.querySelector<HTMLElement>('button[type="button"]:not([disabled])')
    if (firstInput) {
      firstInput.focus({ preventScroll: true })
      if (firstInput instanceof HTMLInputElement && firstInput.type !== "date") {
        firstInput.select?.()
      }
      return
    }
  }
  panel.focus({ preventScroll: true })
}

// immediate: some consumers (e.g. BankAccountPicker) mount with the modal
// already open, so there is no false→true transition to observe.
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) nextTick(() => focusInitial())
  },
  { immediate: true },
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.2s ease;
}

.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-slide-up-enter-active,
.modal-slide-up-leave-active {
  transition: transform 0.3s ease;
}

.modal-slide-up-enter-from,
.modal-slide-up-leave-to {
  transform: translateY(100%);
}
</style>
